import { useEffect, useState } from "react";
import { getThemeConfig } from "themes/getThemeConfig";
import deDE from "antd/es/locale/de_DE";
import enGB from "antd/es/locale/en_GB";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import "dayjs/locale/de";
import { ThemeConfig, ConfigProvider } from "antd";
import AntdModal from "Components/General/Modal/AntdModal";
import { ModalProvider } from "Components/General/Modal/ModalContext";
import { CustomBrowserRouter } from "Config/CustomBrowserRouter";
import i18nConfig from "Config/i18n";
// import { I18nextProvider } from "react-i18next";
import { loginSettingsData, userInfoStore } from "Stores";
import { featureFlagsStore } from "Stores/FeatureFlagsStore";
import { Loader } from "Components/General/Loader";
import { observer } from "mobx-react";
import { FileUpload } from "Pages/Project/Documents/FileUpload/FileUpload";

const route = (
  <div className="login-form">
    <Loader />
  </div>
);

const getLocale = (locale: string) => {
  if (locale === "de") {
    return deDE;
  } else {
    return enGB;
  }
};

// Cache-busting token for favicons. Browsers (and our service worker's image cache)
// cache favicons very aggressively by URL, so replacing a file at the same path is
// not enough for clients to pick it up. Bump this whenever the favicon assets change.
const FAVICON_VERSION = "20260728";

// Resolve the base path for favicons in public/. The two environments differ:
//  - Vite dev server serves public/ from the domain root, so the app's base segment
//    (e.g. "/testdb20") is a virtual SPA route there and would return the index.html
//    fallback (text/html) instead of the image.
//  - Deployed environments serve the whole app (and its public/ assets) under that base
//    segment, e.g. https://host/testdb20/, so the prefix is required.
// Use API_BASE (the deployment segment), not ROUTER_BASE which may add a /portal/... sub-path
// that assets are not served under. Guard the empty case to avoid a protocol-relative "//" URL.
const getAssetBase = () => {
  if (import.meta.env.DEV) return "";
  return window.GLOBAL.API_BASE ? `/${window.GLOBAL.API_BASE}` : "";
};

// Object mapping themes to their favicon and description configurations
function getFavIconConfig(theme: API_LOGINSETTINGS_THEME) {
  const assetBase = getAssetBase();
  switch (theme) {
    case "PAVE":
      return {
        favicon16: `${assetBase}/pave/favicon-16x16.png`,
        favicon32: `${assetBase}/pave/favicon-32x32.png`,
        appleTouchIcon: `${assetBase}/pave/apple-touch-icon.png`,
        description:
          // eslint-disable-next-line max-len
          "Hier geht es zur Anmeldung in PAVE und Verwaltung Ihrer Bauprojekte. PAVE sorgt dank Workflows, KI und BIM-Funktionen für eine effiziente Projektsteuerung.",
      };
    default:
      return {
        favicon16: `${assetBase}/favicon-16x16.png`,
        favicon32: `${assetBase}/favicon-32x32.png`,
        appleTouchIcon: `${assetBase}/apple-touch-icon.png`,
        description: "PMG Project Management",
      };
  }
}

// Function to update favicons and description
const updateFavicons = (theme: API_LOGINSETTINGS_THEME) => {
  const config = getFavIconConfig(theme);
  const withVersion = (url: string) => `${url}?v=${FAVICON_VERSION}`;

  // Simply setting href on the existing (legacy rel="shortcut icon") links is unreliable:
  // most browsers will not re-fetch the tab icon. Remove all existing icon links and append
  // fresh <link> nodes so the browser is forced to re-evaluate and load the new favicon.
  document
    .querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
    .forEach((el) => el.parentNode?.removeChild(el));

  const addIcon = (rel: string, href: string, sizes?: string) => {
    const link = document.createElement("link");
    link.rel = rel;
    link.type = "image/png";
    if (sizes) link.setAttribute("sizes", sizes);
    link.href = withVersion(href);
    document.head.appendChild(link);
  };

  addIcon("icon", config.favicon32, "32x32");
  addIcon("icon", config.favicon16, "16x16");
  addIcon("apple-touch-icon", config.appleTouchIcon, "180x180");

  // Update meta description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement("meta");
    metaDescription.setAttribute("name", "description");
    document.head.appendChild(metaDescription);
  }
  metaDescription.setAttribute("content", config.description);
};

export const AllProvidersProvider = observer(({ children }) => {
  const [language, setLanguage] = useState(localStorage.getItem("i18nextLng") || "de");
  const [themeConfig, setThemeConfig] = useState<ThemeConfig>();
  const [currentTheme, setCurrentTheme] = useState<API_LOGINSETTINGS_THEME>("DEFAULT");

  const handleAntLanguageChange = (newLang: string) => {
    setLanguage(newLang);
    setHtmlLang(newLang);
  };

  const setHtmlLang = (newLang: string) => {
    const lang = newLang === "en" ? "en-US" : newLang;
    document.querySelector("html").lang = lang;
  };

  useEffect(() => {
    i18nConfig.on("languageChanged", handleAntLanguageChange);
    userInfoStore.initStatusChecker();
    const stopFeatureFlagsSync = featureFlagsStore.startSessionSync();
    setHtmlLang(language);
    let newLanguage = language.toLowerCase();
    if (newLanguage === "en") {
      newLanguage = "en-gb";
    }
    dayjs.locale(newLanguage); // important!
    return () => {
      i18nConfig.off("languageChanged", handleAntLanguageChange);
      stopFeatureFlagsSync();
    };
  }, []);

  useEffect(() => {
    const theme = loginSettingsData.theme;
    if (theme) {
      updateFavicons(loginSettingsData.theme);
      document.documentElement.dataset.theme = theme.toLowerCase();
    }
    if (theme !== currentTheme || !themeConfig) {
      setCurrentTheme(theme);
      setThemeConfig(getThemeConfig(theme));
    }
  }, [loginSettingsData.theme]);

  return (
    <ModalProvider>
      <ConfigProvider
        locale={getLocale(language)}
        theme={themeConfig}
        csp={{ nonce: "12345" }}
        modal={{ mask: { blur: false } }}
        drawer={{ mask: { blur: false } }}
        tag={{ variant: "outlined" }}
      >
        <AntdModal />
        <FileUpload />
        <CustomBrowserRouter basename={window.GLOBAL.ROUTER_BASE}>
          {userInfoStore.initStatusCheckComplete ? children : route}
        </CustomBrowserRouter>
      </ConfigProvider>
    </ModalProvider>
  );
});

export default AllProvidersProvider;
